import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Button, 
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Badge,
  Chip,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  CreditCard as PaymentIcon,
  Search as SearchIcon,
  Restaurant as RestaurantIcon,
  LocalMall as RetailIcon,
  Print as PrintIcon,
  TableBar as TableIcon,
  CheckCircle as CompleteIcon,
  ArrowBack as BackIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  LocalDrink as DrinkIcon,
  DinnerDining as DiningIcon,
  Cake as DessertIcon,
  Fastfood as FastfoodIcon,
  LocalCafe as CoffeeIcon
} from '@mui/icons-material';
import { Product, getActiveProducts, getProductsByCategory } from '../../firebase/services/productService';

// Cart item type
interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  notes: string;
  itemType: 'food' | 'retail';
  modifiers?: Array<{
    name: string;
    options: Array<{
      name: string;
      price: number;
    }>;
  }>;
}

// Category type
interface ItemCategory {
  id: string;
  name: string;
  color: string;
  icon?: React.ReactNode;
  parentId?: string;
  isRoot: boolean;
}

// Define all categories
const ALL_CATEGORIES: ItemCategory[] = [
  // Root categories
  { id: 'food', name: 'Food & Drink', color: '#4caf50', icon: <DiningIcon />, isRoot: true },
  { id: 'retail', name: 'Retail', color: '#2196f3', icon: <RetailIcon />, isRoot: true },
  
  // Food subcategories
  { id: 'Appetizers', name: 'Appetizers', color: '#4caf50', parentId: 'food', isRoot: false },
  { id: 'Entrees', name: 'Entrées', color: '#ff5722', parentId: 'food', isRoot: false },
  { id: 'Sides', name: 'Sides', color: '#ffc107', parentId: 'food', isRoot: false },
  { id: 'Desserts', name: 'Desserts', color: '#673ab7', icon: <DessertIcon />, parentId: 'food', isRoot: false },
  { id: 'Drinks', name: 'Drinks', color: '#2196f3', icon: <DrinkIcon />, parentId: 'food', isRoot: false },
  { id: 'Coffee', name: 'Coffee', color: '#795548', icon: <CoffeeIcon />, parentId: 'food', isRoot: false },
  { id: 'Alcoholic Beverages', name: 'Alcoholic Beverages', color: '#9c27b0', parentId: 'food', isRoot: false },
  
  // Retail subcategories
  { id: 'Apparel', name: 'Apparel', color: '#3f51b5', parentId: 'retail', isRoot: false },
  { id: 'Beach', name: 'Beach', color: '#03a9f4', parentId: 'retail', isRoot: false },
  { id: 'Boating', name: 'Boating', color: '#009688', parentId: 'retail', isRoot: false },
  { id: 'Snacks', name: 'Snacks', color: '#ff9800', icon: <FastfoodIcon />, parentId: 'retail', isRoot: false },
  { id: 'Beverages', name: 'Beverages', color: '#e91e63', icon: <DrinkIcon />, parentId: 'retail', isRoot: false },
  { id: 'Souvenirs', name: 'Souvenirs', color: '#8bc34a', parentId: 'retail', isRoot: false },
  { id: 'Equipment', name: 'Equipment', color: '#607d8b', parentId: 'retail', isRoot: false }
];

// Tables for dine-in
interface Table {
  id: string;
  number: number;
  status: 'available' | 'occupied' | 'reserved';
  seats: number;
}

const TABLES: Table[] = [
  { id: '1', number: 1, status: 'available', seats: 2 },
  { id: '2', number: 2, status: 'occupied', seats: 2 },
  { id: '3', number: 3, status: 'available', seats: 4 },
  { id: '4', number: 4, status: 'reserved', seats: 4 },
  { id: '5', number: 5, status: 'occupied', seats: 6 },
  { id: '6', number: 6, status: 'available', seats: 6 },
];

const UnifiedPOSContent: React.FC = () => {
  const theme = useTheme();
  
  // State variables
  const [currentView, setCurrentView] = useState<'categories' | 'items'>('categories');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [checkoutMode, setCheckoutMode] = useState<'dineIn' | 'takeout' | 'retail'>('retail');
  const [customerInfoDialogOpen, setCustomerInfoDialogOpen] = useState(false);
  
  // Product state
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Load all active products on component mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const activeProducts = await getActiveProducts();
        setProducts(activeProducts);
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    
    loadProducts();
  }, []);
  
  // Load products for a specific category when active category changes
  useEffect(() => {
    const loadCategoryProducts = async () => {
      if (!activeCategory || currentView !== 'items') return;
      
      try {
        setLoading(true);
        const categoryProducts = await getProductsByCategory(activeCategory);
        setProducts(categoryProducts);
      } catch (err) {
        console.error(`Error loading products for category ${activeCategory}:`, err);
        setError(`Failed to load products for ${activeCategory}`);
      } finally {
        setLoading(false);
      }
    };
    
    loadCategoryProducts();
  }, [activeCategory, currentView]);
  
  // Get current categories based on view
  const getCurrentCategories = () => {
    if (currentView === 'categories') {
      return ALL_CATEGORIES.filter(category => category.isRoot);
    } else if (activeCategory) {
      return ALL_CATEGORIES.filter(category => category.parentId === activeCategory);
    }
    return [];
  };
  
  // Get current items based on active category
  const getCurrentItems = () => {
    if (!activeCategory) return [];
    
    return products.filter(item => {
      // For the items view, filter by the exact category
      const matchesCategory = item.category === activeCategory;
      const matchesSearch = searchTerm 
        ? item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      
      return matchesCategory && matchesSearch;
    });
  };
  
  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    const category = ALL_CATEGORIES.find(cat => cat.id === categoryId);
    
    if (category) {
      setActiveCategory(categoryId);
      
      // Check if the category has subcategories
      const hasSubcategories = ALL_CATEGORIES.some(cat => cat.parentId === categoryId);
      
      if (hasSubcategories) {
        setCurrentView('categories');
      } else {
        setCurrentView('items');
      }
    }
  };
  
  // Handle back button
  const handleBackButton = () => {
    if (currentView === 'items') {
      // Go back to categories
      const currentCategory = ALL_CATEGORIES.find(cat => cat.id === activeCategory);
      if (currentCategory && currentCategory.parentId) {
        setActiveCategory(currentCategory.parentId);
      } else {
        setCurrentView('categories');
        setActiveCategory(null);
      }
    } else if (activeCategory) {
      // Go back to root categories
      const currentCategory = ALL_CATEGORIES.find(cat => cat.id === activeCategory);
      if (currentCategory && currentCategory.parentId) {
        setActiveCategory(currentCategory.parentId);
      } else {
        setActiveCategory(null);
      }
    }
  };
  
  // Add item to cart
  const addToCart = (item: Product) => {
    const existingItemIndex = cart.findIndex(cartItem => cartItem.id === item.id);
    
    if (existingItemIndex >= 0) {
      // Item already in cart, increase quantity
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity += 1;
      setCart(updatedCart);
    } else {
      // Add new item to cart
      setCart([...cart, { 
        id: item.id!,
        name: item.name,
        price: item.price,
        quantity: 1,
        notes: '',
        itemType: item.itemType,
        modifiers: []
      }]);
    }
  };
  
  // Remove item from cart
  const removeFromCart = (id: string) => {
    const existingItemIndex = cart.findIndex(item => item.id === id);
    
    if (existingItemIndex >= 0) {
      const updatedCart = [...cart];
      if (updatedCart[existingItemIndex].quantity > 1) {
        // Decrease quantity
        updatedCart[existingItemIndex].quantity -= 1;
      } else {
        // Remove item completely
        updatedCart.splice(existingItemIndex, 1);
      }
      setCart(updatedCart);
    }
  };
  
  // Delete item from cart
  const deleteFromCart = (id: string) => {
    setCart(cart.filter(item => item.id !== id));
  };
  
  // Update item notes
  const updateItemNotes = (id: string, notes: string) => {
    setCart(cart.map(item => 
      item.id === id ? { ...item, notes } : item
    ));
  };
  
  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    
    // If we're in category view and there's a search term, switch to items view
    if (currentView === 'categories' && event.target.value && activeCategory) {
      setCurrentView('items');
    }
  };
  
  // Calculate subtotal
  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };
  
  // Calculate tax
  const calculateTax = (subtotal: number) => {
    return subtotal * 0.0725; // 7.25% tax rate
  };
  
  // Calculate total
  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax(subtotal);
    return subtotal + tax;
  };
  
  // Handle table selection
  const selectTable = (table: Table) => {
    setSelectedTable(table);
    setTableDialogOpen(false);
    setCheckoutMode('dineIn');
  };
  
  // Open checkout menu
  const handleCheckoutClick = () => {
    if (cart.length === 0) {
      alert('Your cart is empty');
      return;
    }
    
    // If cart has food items, prompt for dine-in or takeout
    const hasFoodItems = cart.some(item => item.itemType === 'food');
    
    if (hasFoodItems) {
      // For food items, check if we need a table
      if (checkoutMode === 'dineIn' && !selectedTable) {
        setTableDialogOpen(true);
      } else {
        // Handle checkout
        processCheckout();
      }
    } else {
      // For retail only, just get customer info
      setCheckoutMode('retail');
      setCustomerInfoDialogOpen(true);
    }
  };
  
  // Process the checkout
  const processCheckout = () => {
    const orderSummary = `Order type: ${checkoutMode}${selectedTable ? ` - Table ${selectedTable.number}` : ''}
      ${customerName ? `Customer: ${customerName}` : ''}
      Total: $${calculateTotal().toFixed(2)}`;
    
    alert(`Order completed!\n${orderSummary}`);
    
    // Clear cart
    setCart([]);
    
    // Reset other states as needed
    if (checkoutMode !== 'dineIn') {
      setSelectedTable(null);
    }
    setCustomerName('');
  };
  
  // Get title for current view
  const getViewTitle = () => {
    if (currentView === 'categories') {
      if (activeCategory) {
        const category = ALL_CATEGORIES.find(cat => cat.id === activeCategory);
        return category ? category.name : 'Categories';
      }
      return 'Main Menu';
    } else {
      const category = ALL_CATEGORIES.find(cat => cat.id === activeCategory);
      return category ? category.name : 'Items';
    }
  };
  
  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.default',
      color: 'text.primary'
    }}>
      {/* Top Navigation Bar */}
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Back button when needed */}
            {(currentView === 'items' || activeCategory) && (
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleBackButton}
                sx={{ mr: 2 }}
              >
                <BackIcon />
              </IconButton>
            )}
            
            <Typography variant="h6" sx={{ mr: 2 }}>
              {getViewTitle()}
            </Typography>
            
            {/* Table indicator */}
            {selectedTable && (
              <Chip 
                label={`Table ${selectedTable.number}`} 
                color="primary" 
                variant="outlined"
                onClick={() => setTableDialogOpen(true)}
                sx={{ mr: 2 }}
              />
            )}
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <TextField
            placeholder="Search..."
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: 200, mr: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          
          <Badge badgeContent={cart.length} color="error" sx={{ mr: 2 }}>
            <CartIcon />
          </Badge>
          
          <IconButton color="inherit">
            <SettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex' }}>
        {/* Main Content Area */}
        <Box sx={{ flexGrow: 1, p: 2, overflow: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {/* Grid of buttons - either categories or items depending on view */}
          <Grid container spacing={2}>
            {currentView === 'categories' ? (
              // Show categories
              (getCurrentCategories().map((category) => (
                <Grid
                  key={category.id}
                  xs={6}
                  sm={4}
                  md={3}
                  lg={2}>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: category.color || theme.palette.primary.main,
                      height: 120,
                      display: 'flex',
                      flexDirection: 'column',
                      textAlign: 'center',
                      whiteSpace: 'normal',
                      '&:hover': {
                        bgcolor: category.color ? `${category.color}DD` : undefined,
                      }
                    }}
                    onClick={() => handleCategorySelect(category.id)}
                  >
                    {category.icon && (
                      <Box sx={{ mb: 1, fontSize: '2rem' }}>
                        {category.icon}
                      </Box>
                    )}
                    {category.name}
                  </Button>
                </Grid>
              )))
            ) : loading ? (
              // Show loading indicator
              (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', p: 5 }}>
                <CircularProgress />
              </Box>)
            ) : getCurrentItems().length === 0 ? (
              // Show empty state
              (<Box sx={{ width: '100%', p: 5, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No products found in this category
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Add products using the Products management page
                </Typography>
              </Box>)
            ) : (
              // Show items
              (getCurrentItems().map((item) => (
                <Grid
                  key={item.id}
                  xs={6}
                  sm={4}
                  md={3}
                  lg={2}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        boxShadow: 4
                      }
                    }}
                    onClick={() => addToCart(item)}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {item.name}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        ${item.price.toFixed(2)}
                      </Typography>
                      
                      {/* Show inventory for retail items */}
                      {item.itemType === 'retail' && item.inventory !== undefined && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          In stock: {item.inventory}
                        </Typography>
                      )}
                      
                      {item.sku && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          SKU: {item.sku}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )))
            )}
          </Grid>
        </Box>
        
        {/* Right Panel: Cart */}
        <Paper 
          sx={{ 
            width: 300, 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            borderLeft: 1,
            borderColor: 'divider'
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Cart
            </Typography>
            
            {/* Order mode selection */}
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                size="small" 
                variant={checkoutMode === 'dineIn' ? 'contained' : 'outlined'}
                startIcon={<TableIcon />}
                onClick={() => {
                  setCheckoutMode('dineIn');
                  if (!selectedTable) setTableDialogOpen(true);
                }}
              >
                Dine-In
              </Button>
              
              <Button 
                size="small" 
                variant={checkoutMode === 'takeout' ? 'contained' : 'outlined'}
                startIcon={<RestaurantIcon />}
                onClick={() => setCheckoutMode('takeout')}
              >
                Takeout
              </Button>
              
              <Button 
                size="small" 
                variant={checkoutMode === 'retail' ? 'contained' : 'outlined'}
                startIcon={<RetailIcon />}
                onClick={() => setCheckoutMode('retail')}
              >
                Retail
              </Button>
            </Box>
          </Box>
          
          {/* Cart Items */}
          <List sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
            {cart.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                Cart is empty
              </Typography>
            ) : (
              cart.map((item) => (
                <ListItem 
                  key={item.id}
                  divider
                  secondaryAction={
                    <Box>
                      <IconButton 
                        edge="end" 
                        size="small" 
                        color="primary"
                        onClick={() => {
                          const product = products.find(p => p.id === item.id);
                          if (product) addToCart(product);
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        size="small" 
                        onClick={() => removeFromCart(item.id)}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        size="small" 
                        color="error"
                        onClick={() => deleteFromCart(item.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ pr: 8 }}>
                        <Typography variant="body1" noWrap>
                          {item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          ${(item.price * item.quantity).toFixed(2)}
                        </Typography>
                      </Box>
                    }
                    secondary={item.notes || undefined}
                  />
                </ListItem>
              ))
            )}
          </List>
          
          {/* Totals and Actions */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            {/* Customer name if available */}
            {customerName && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Customer:
                </Typography>
                <Typography variant="body1">
                  {customerName}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Subtotal</Typography>
              <Typography variant="body1">${calculateSubtotal().toFixed(2)}</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Tax</Typography>
              <Typography variant="body1">${calculateTax(calculateSubtotal()).toFixed(2)}</Typography>
            </Box>
            
            <Divider sx={{ my: 1 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Total</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>${calculateTotal().toFixed(2)}</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button 
                variant="contained" 
                startIcon={<PaymentIcon />}
                onClick={handleCheckoutClick}
                disabled={cart.length === 0}
                fullWidth
              >
                Checkout
              </Button>
              
              <Button 
                variant="outlined" 
                color="primary"
                fullWidth
                startIcon={<TableIcon />}
                onClick={() => setTableDialogOpen(true)}
              >
                Select Table
              </Button>
              
              <Button 
                variant="outlined" 
                color="error"
                fullWidth
                onClick={() => setCart([])}
                disabled={cart.length === 0}
              >
                Clear Cart
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
      {/* Table Selection Dialog */}
      <Dialog open={tableDialogOpen} onClose={() => setTableDialogOpen(false)} maxWidth="md">
        <DialogTitle>Select a Table</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {TABLES.map((table) => (
              <Grid
                key={table.id}
                xs={4}
                sm={3}
                md={2}>
                <Button
                  variant={selectedTable?.id === table.id ? "contained" : "outlined"}
                  fullWidth
                  sx={{ 
                    height: 100,
                    bgcolor: 
                      table.status === 'occupied' 
                        ? theme.palette.error.light 
                        : table.status === 'reserved' 
                          ? theme.palette.warning.light
                          : undefined,
                    color: table.status !== 'available' ? 'white' : undefined,
                    flexDirection: 'column',
                    pointerEvents: table.status === 'occupied' ? 'none' : undefined,
                    opacity: table.status === 'occupied' ? 0.7 : 1,
                  }}
                  onClick={() => selectTable(table)}
                  disabled={table.status === 'occupied'}
                >
                  <Typography variant="subtitle1">
                    Table {table.number}
                  </Typography>
                  <Typography variant="caption">
                    {table.seats} seats
                  </Typography>
                  <Chip 
                    label={table.status} 
                    size="small"
                    color={
                      table.status === 'available' 
                        ? 'success' 
                        : table.status === 'reserved' 
                          ? 'warning' 
                          : 'error'
                    }
                    sx={{ mt: 1 }}
                  />
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      {/* Customer Info Dialog */}
      <Dialog open={customerInfoDialogOpen} onClose={() => setCustomerInfoDialogOpen(false)}>
        <DialogTitle>Customer Information</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Customer Name"
            fullWidth
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerInfoDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setCustomerInfoDialogOpen(false);
              processCheckout();
            }}
            variant="contained"
          >
            Complete Sale
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const UnifiedPOS: React.FC = () => {
  return (
    <UnifiedPOSContent />
  );
};

export default UnifiedPOS; 